/**
 * Turn an audit row's actor into something an operator recognises.
 *
 * The audit record stores only the opaque ZITADEL subject, so the Actor
 * column read `387121678485947655` -- correct, permanent, and useless
 * to the person whose own actions it was listing. The API now resolves
 * that subject against the directory of everyone who has signed in;
 * this decides what to show with whatever came back.
 *
 * ORDER: display name, then email, then the two cases where no
 * directory entry can ever exist.
 *
 * THE PLATFORM'S OWN ACTIONS are not people and must not read as a
 * failed lookup. `system:fire_mode` writes the audit rows for the
 * automatic parts of a fire response, `system:idle_behavior` for a
 * robot sent to its charger by policy rather than by a person, and
 * `system:scheduler` for every task a calendar entry starts. They get a
 * plain English name and keep the raw id in the title, because "who did
 * this" having the answer "nobody, the platform did" is exactly the
 * thing an operator reading an audit trail needs to be told.
 */
export interface ActorFields {
  readonly actor_sub: string;
  readonly actor_email?: string | null;
  readonly actor_display_name?: string | null;
}

const SYSTEM_ACTORS: Readonly<Record<string, string>> = {
  'system:fire_mode': 'Platform — fire mode',
  'system:idle_behavior': 'Platform — automatic charging',
  // Every task a calendar entry starts is written by this one. It is
  // the busiest synthetic actor at a site running schedules, and it was
  // missed on the first pass -- `system_publish` enforces the `system:`
  // prefix but nothing enumerates the actors, so the list has to be
  // kept in step with `backend/api_server/*.py` by hand. A test walks
  // the backend for `system:` literals and fails if one is unlisted.
  'system:scheduler': 'Platform — scheduled task',
  'system:lift_rules': 'Platform — lift rules',
};

/** The label for the Actor column, the CSV and the search index. */
export function formatActor(e: ActorFields): string {
  const name = e.actor_display_name?.trim();
  if (name) return name;
  const email = e.actor_email?.trim();
  if (email) return email;
  const system = SYSTEM_ACTORS[e.actor_sub];
  if (system) return system;
  // An unresolved human subject. Showing the raw id is the honest
  // answer: something did this and we cannot name it, which is worth
  // seeing rather than hiding behind "Unknown".
  return e.actor_sub;
}

/**
 * What to put in `title=`. Empty when the label already IS the subject,
 * so a row with nothing more to say carries no tooltip.
 */
export function actorTitle(e: ActorFields): string {
  const label = formatActor(e);
  if (label === e.actor_sub) return '';
  const email = e.actor_email?.trim();
  return email && email !== label ? `${email} · ${e.actor_sub}` : e.actor_sub;
}

/** Everything about the actor that a text search should match. */
export function actorSearchText(e: ActorFields): string {
  return [e.actor_sub, e.actor_email ?? '', e.actor_display_name ?? '', formatActor(e)].join(' ');
}
